// Surveys: the building blocks, their labels, and when a block shows. The server checks the same
// shapes (server/services/surveyContent.js) and reads conditions the same way.

export const QUESTION_KINDS = [
  {
    value: 'choice',
    label: 'Dropdown choice',
    icon: 'bi-menu-button-wide',
    hint: 'They pick from a list you write. Allow more than one if it fits.',
  },
  { value: 'text', label: 'Text answer', icon: 'bi-input-cursor-text', hint: 'They type an answer. Pick a single line or multiple lines below.' },
  { value: 'rating', label: 'Star rating', icon: 'bi-star', hint: 'One to five stars.' },
  {
    value: 'ranking',
    label: 'Ranking',
    icon: 'bi-list-ol',
    hint: 'They drag your options into order, most important first.',
  },
  {
    value: 'signature',
    label: 'Signature',
    icon: 'bi-pen',
    hint: 'They type their name to attest to a statement, e.g. that they have read a policy.',
  },
];

export const CONTENT_TYPES = [
  { value: 'text', label: 'Text', icon: 'bi-text-paragraph' },
  { value: 'link', label: 'Link', icon: 'bi-link-45deg' },
  { value: 'image', label: 'Image', icon: 'bi-image' },
];

export const kindInfo = (kind) => QUESTION_KINDS.find((k) => k.value === kind);
export const contentInfo = (type) => CONTENT_TYPES.find((t) => t.value === type);

const isTextQuestion = (block) => block?.type === 'question' && block.kind === 'text';

export const blockLabel = (block) => {
  if (isTextQuestion(block)) return block.multiline ? 'Text answer (multiple lines)' : 'Text answer (single line)';
  return block?.type === 'question' ? kindInfo(block.kind)?.label || 'Question' : contentInfo(block?.type)?.label || 'Block';
};

export const blockIcon = (block) => {
  if (isTextQuestion(block)) return block.multiline ? 'bi-text-paragraph' : 'bi-input-cursor-text';
  return block?.type === 'question' ? kindInfo(block.kind)?.icon || 'bi-question-circle' : contentInfo(block?.type)?.icon || 'bi-square';
};

export const STATE_LABELS = { draft: 'Draft', open: 'Open', closed: 'Closed' };

// How often a recurring survey goes out, in words.
export const scheduleLabel = (everyMonths) => {
  if (everyMonths === 1) return 'Every month';
  if (everyMonths === 3) return 'Every quarter';
  if (everyMonths === 12) return 'Every year';
  return `Every ${everyMonths} months`;
};

// Open surveys sent to me that I haven't sent back yet (from useMySurveys).
export const countSurveysWaiting = (mine) => (Array.isArray(mine) ? mine : [])
  .filter((survey) => survey.state === 'open' && survey.myStatus !== 'submitted').length;

let nextLocalId = 1;
// A short id for a new block or option; the server keeps ids it is given.
export const newId = (prefix = 'b') => `${prefix}${Date.now().toString(36)}${(nextLocalId++).toString(36)}`;

export const newOption = (label = '') => ({ id: newId('o'), label });

export const newBlock = (type, kind) => {
  const base = { id: newId(), type, condition: null };
  if (type === 'text') return { ...base, text: '' };
  if (type === 'link') return { ...base, url: '', label: '' };
  if (type === 'image') return { ...base, assetId: null, url: '', alt: '', caption: '' };
  const question = { ...base, kind, prompt: '', help: '', required: false };
  if (kind === 'choice') return { ...question, options: [newOption('Option 1'), newOption('Option 2')], multiple: false };
  if (kind === 'ranking') return { ...question, options: [newOption('Option 1'), newOption('Option 2'), newOption('Option 3')] };
  if (kind === 'text') return { ...question, multiline: false };
  if (kind === 'signature') return { ...question, statement: '' };
  return question;
};

// ---- Conditions: show a block only when an earlier question was answered a certain way ----

export const conditionOps = (question) => {
  if (question?.kind === 'choice') {
    return question.multiple
      ? [
          { value: 'is', label: 'includes' },
          { value: 'is_not', label: "doesn't include" },
          { value: 'answered', label: 'is answered' },
          { value: 'not_answered', label: 'is not answered' },
        ]
      : [
          { value: 'is', label: 'is' },
          { value: 'is_not', label: 'is not' },
          { value: 'answered', label: 'is answered' },
          { value: 'not_answered', label: 'is not answered' },
        ];
  }
  if (question?.kind === 'rating') {
    return [
      { value: 'eq', label: 'is' },
      { value: 'gte', label: 'is at least' },
      { value: 'lte', label: 'is at most' },
      { value: 'answered', label: 'is answered' },
      { value: 'not_answered', label: 'is not answered' },
    ];
  }
  return [
    { value: 'answered', label: 'is answered' },
    { value: 'not_answered', label: 'is not answered' },
  ];
};

export const opNeedsValue = (op) => ['is', 'is_not', 'eq', 'gte', 'lte'].includes(op);

export const isAnswered = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Boolean(value.name);
  return true;
};

const isVisible = (block, answers, visible) => {
  const c = block?.condition;
  if (!c || !c.questionId) return true;
  if (!visible.has(c.questionId)) return false;
  const answer = answers?.[c.questionId];
  switch (c.op) {
    case 'answered':
      return isAnswered(answer);
    case 'not_answered':
      return !isAnswered(answer);
    case 'is':
      return Array.isArray(answer) ? answer.includes(c.value) : answer === c.value;
    case 'is_not':
      return Array.isArray(answer) ? !answer.includes(c.value) : isAnswered(answer) && answer !== c.value;
    case 'eq':
      return Number(answer) === Number(c.value);
    case 'gte':
      return Number(answer) >= Number(c.value);
    case 'lte':
      return isAnswered(answer) && Number(answer) <= Number(c.value);
    default:
      return true;
  }
};

// The ids of the blocks that show for these answers.
export const visibleBlockIds = (content, answers) => {
  const visible = new Set();
  for (const block of Array.isArray(content) ? content : []) {
    if (isVisible(block, answers, visible)) visible.add(block.id);
  }
  return visible;
};

// "Question 2", counting questions only, by block id.
export const questionNumbers = (content) => {
  const numbers = new Map();
  let n = 0;
  for (const block of Array.isArray(content) ? content : []) {
    if (block.type === 'question') numbers.set(block.id, (n += 1));
  }
  return numbers;
};

// A condition in words: "Only if Q2 is Yes".
export const describeCondition = (condition, content) => {
  if (!condition?.questionId) return '';
  const numbers = questionNumbers(content);
  const source = content.find((b) => b.id === condition.questionId);
  if (!source) return 'Only if an earlier question… (that question was removed)';
  const op = conditionOps(source).find((o) => o.value === condition.op)?.label || condition.op;
  let value = '';
  if (opNeedsValue(condition.op)) {
    value = source.kind === 'choice'
      ? ` "${source.options?.find((o) => o.id === condition.value)?.label || '…'}"`
      : ` ${condition.value} star${Number(condition.value) === 1 ? '' : 's'}`;
  }
  return `Only if Q${numbers.get(source.id)} ${op}${value}`;
};

export const hasSignature = (content) =>
  (Array.isArray(content) ? content : []).some((b) => b.type === 'question' && b.kind === 'signature');

// What anonymous means, said the same way to the people taking it and the person building it.
export const ANONYMOUS_NOTE =
  "This survey is anonymous. Your name is not linked to your answers: they're stored apart from who sent them, so nobody, including the people who run the survey, can see who said what.";

// What still needs doing before a survey can go out, in words, or ''.
export const publishProblem = (survey) => {
  if (!survey?.title?.trim()) return 'Give the survey a title.';
  if (!survey.content.some((b) => b.type === 'question')) return 'Add at least one question.';
  if (survey.anonymous && hasSignature(survey.content)) return 'Remove the signature question: an anonymous survey can\'t ask for a name.';
  return '';
};

export const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
